import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { IconButton, makeStyles, useMediaQuery, useTheme } from "@material-ui/core";
import { Button, ButtonGroup, DialogContent, Grid, Checkbox, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import productPageStyle from "features/products/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogActions from "@material-ui/core/DialogActions";
import {
  Autocomplete,
  CustomTextField,
  DateTime,
  LoadingFakeText,
} from "@totalsoft/rocket-webapp-components-deprecated";
import { emptyObject, emptyString } from "constants/defaultValues";
import { onLensSliderChange, onNumericTextFieldChange, onCheckBoxChange } from "utils/propertyChangeAdapters";
import { getErrors, isValid } from "@totalsoft/pure-validations-react";
import { get, sequence, set } from "@totalsoft/react-state-lens";
import ProductSchedule from "../ProductSchedule";
import SimplebarWrapper from "features/common/components/ScrollbarWrapper";
import EditProductActions from "../EditProductActions";
import { Close } from "@material-ui/icons";
import useUserData from "hooks/useUserData";
import EditInsuranceDetails from "../EditInsuranceDetails";
import SimpleSliderWrapper from "../SimpleSliderWrapper";

const useStyles = makeStyles(productPageStyle);

function BasicEditProduct(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { allowInsurance } = useUserData();

  const {
    openEditProductLens,
    productLens,
    quote,
    productPeriodicities,
    productScheduleTypes,
    settingsLens,
    autoAddedInsuranceTypes,
  } = props;

  const { productValidation, productDirtyInfo, onSave, onComputeSchedule } = props;
  const product = productLens |> get;
  const loading = props.loading || !quote || !product;
  const saving = props.saving;
  const useDownPaymentPercent = settingsLens.useDownPaymentPercent |> get;
  const useResidualPercent = settingsLens.useResidualPercent |> get;
  const settings = settingsLens |> get || emptyObject;

  const handleActions = useCallback(
    (event) => {
      switch (event.currentTarget.id) {
        case "down-payment-percent":
          (settingsLens.useDownPaymentPercent |> set)(true);
          break;
        case "down-payment-abs":
          (settingsLens.useDownPaymentPercent |> set)(false);
          break;
        case "balloon-percent":
          (settingsLens.useResidualPercent |> set)(true);
          break;
        case "balloon-abs":
          (settingsLens.useResidualPercent |> set)(false);
          break;
      }
    },
    [settingsLens.useDownPaymentPercent, settingsLens.useResidualPercent]
  );

  const handleClose = useCallback(() => {
    if (saving) return;
    (openEditProductLens |> set)();
  }, [openEditProductLens, saving]);

  const handleScheduleTypeChange = useCallback(
    (scheduleTypeId) => {
      (productLens.scheduleTypeId |> set)(scheduleTypeId);
      const scheduleType = productScheduleTypes.find((x) => x.id === scheduleTypeId);
      (productLens.scheduleIsConfigurable |> set)(scheduleType?.isConfigurable);
      (settingsLens.years |> sequence).forEach((yearLens) => {
        (yearLens.months |> sequence).forEach((monthLens) => (monthLens.selected |> set)(false));
      });
      (productLens.cellParamsConfigurations |> set)([]);
    },
    [
      productLens.scheduleTypeId,
      productScheduleTypes,
      productLens.scheduleIsConfigurable,
      settingsLens.years,
      productLens.cellParamsConfigurations,
    ]
  );

  const dialogPaperProps = useMemo(() => ({ style: { overflowY: "visible", width: "500px", maxHeight: "100vh" } }), []);

  return (
    <>
      <Dialog
        maxWidth={"sm"}
        fullScreen={fullScreen}
        fullWidth={true}
        open={true}
        PaperProps={dialogPaperProps}
        onClose={handleClose}
      >
        <DialogTitle>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h4">{t("EditProduct.EditProductTitle")}</Typography>
            </Grid>
            <Grid item>
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        {(loading || !settings?.product?.productId) && (
          <DialogContent dividers>
            <LoadingFakeText lines={10} />
          </DialogContent>
        )}
        {!loading && settings?.product?.productId && (
          <DialogContent dividers className={classes.basicDialogContent}>
            <Grid container spacing={2} direction={fullScreen ? "column" : "row"}>
              <Grid item xs={fullScreen} className={classes.editProductParams}>
                <SimplebarWrapper className={classes.scrollbar}>
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                    className={classes.basicPadding}
                  >
                    <Grid item xs={12}>
                      <Autocomplete
                        options={productScheduleTypes}
                        value={product?.scheduleTypeId || emptyString}
                        onChange={handleScheduleTypeChange}
                        valueKey="id"
                        labelKey="caption"
                        label={t("EditProduct.Schedule")}
                        simpleValue={true}
                        noOptionsText={t("General.AutocompleteNoOptions")}
                      />
                    </Grid>
                    {product?.useVatFinancing && (
                      <Grid item container spacing={1} alignItems="center">
                        <Grid item xs={3}>
                          <Typography className={classes.vatFinancingLabel}>{t("Product.VatFinancing")}</Typography>
                        </Grid>
                        <Grid item xs={9}>
                          <Checkbox
                            checked={(productLens.vatFinancing |> get) || false}
                            onChange={productLens.vatFinancing |> set |> onCheckBoxChange}
                            size="small"
                          />
                        </Grid>
                        {(productLens.vatFinancing |> get) && (
                          <Grid item container alignItems="center">
                            <Grid item xs={5}>
                              <Typography className={classes.vatFinancingLabel}>
                                {t("Product.FinalPurchasePrice")}
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <input
                                className={classes.input}
                                type="text"
                                value={product?.finalPurchasePrice?.toFixed(2)}
                                disabled
                              />
                            </Grid>
                            <Grid item xs={5}>
                              <Typography className={classes.vatFinancingLabel}>
                                {t("Product.FinalDownPayment")}
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <input
                                className={classes.input}
                                type="text"
                                value={product?.finalDownPaymentAmount?.toFixed(2)}
                                disabled
                              />
                            </Grid>
                            <Grid item xs={5}>
                              <Typography className={classes.vatFinancingLabel}>
                                {t("Product.FinancedVATAmount")}
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <input
                                className={classes.input}
                                type="text"
                                value={product?.financedVatAmount?.toFixed(2)}
                                disabled
                              />
                            </Grid>
                          </Grid>
                        )}
                      </Grid>
                    )}
                    {!product?.useRentalDownPayment && useDownPaymentPercent && settings?.enableDownPaymentSlider && (
                      <Grid item xs={8}>
                        <SimpleSliderWrapper
                          label={`${
                            product?.showAmountsWithVAT ? t("Product.DownPaymentWithVAT") : t("Product.DownPayment")
                          } | ${t("DECIMAL2", {
                            number: product?.downPaymentPercent,
                          })}% (${quote?.currencyCode} ${
                            product?.showAmountsWithVAT
                              ? t("DECIMAL2", { number: product?.downPaymentAmountWithVAT })
                              : t("DECIMAL2", { number: product?.downPaymentAmount })
                          })`}
                          min={product?.downPaymentMinPercent}
                          max={product?.downPaymentMaxPercent}
                          value={product?.downPaymentPercent || 0}
                          onChange={onLensSliderChange(productLens.downPaymentPercent)}
                          aria-valuetext="downPaymentSlider"
                          fullWidth={true}
                          showSliderLimits={true}
                          error={!isValid(productValidation?.downPaymentPercent)}
                          helperText={getErrors(productValidation?.downPaymentPercent)}
                          decimalScale={2}
                          step={1}
                          classes={{ root: classes.sliderRoot, thumb: classes.sliderThumb }}
                        />
                      </Grid>
                    )}
                    {!product?.useRentalDownPayment && !useDownPaymentPercent && (
                      <Grid item xs={8}>
                        <CustomTextField
                          label={`${
                            product?.showAmountsWithVAT ? t("Product.DownPaymentWithVAT") : t("Product.DownPayment")
                          } | ${t("DECIMAL2", {
                            number: product?.downPaymentPercent,
                          })}% (${quote?.currencyCode} ${
                            product?.showAmountsWithVAT
                              ? t("DECIMAL2", { number: product?.downPaymentAmountWithVAT })
                              : t("DECIMAL2", { number: product?.downPaymentAmount })
                          })`}
                          fullWidth
                          isNumeric={true}
                          onChange={
                            product?.showAmountsWithVAT
                              ? productLens.downPaymentAmountWithVAT |> set |> onNumericTextFieldChange
                              : productLens.downPaymentAmount |> set |> onNumericTextFieldChange
                          }
                          value={
                            (product?.showAmountsWithVAT
                              ? product?.downPaymentAmountWithVAT
                              : product?.downPaymentAmount) || 0
                          }
                          startAdornment={<Typography variant="h6">{quote?.currencyCode}</Typography>}
                          error={
                            !isValid(
                              product?.showAmountsWithVAT
                                ? productValidation?.downPaymentAmountWithVAT
                                : productValidation?.downPaymentAmount
                            )
                          }
                          helperText={getErrors(
                            product?.showAmountsWithVAT
                              ? productValidation?.downPaymentAmountWithVAT
                              : productValidation?.downPaymentAmount
                          )}
                        />
                      </Grid>
                    )}
                    {!product?.useRentalDownPayment && settings?.enableDownPaymentSlider && (
                      <Grid item xs={4}>
                        <ButtonGroup className={classes.rightAlign}>
                          <Button
                            id="down-payment-percent"
                            variant="contained"
                            size="small"
                            color={useDownPaymentPercent ? "primary" : "inherit"}
                            onClick={handleActions}
                          >
                            {t("EditProduct.Percent")}
                          </Button>
                          <Button
                            id="down-payment-abs"
                            size="small"
                            variant="contained"
                            color={useDownPaymentPercent ? "inherit" : "primary"}
                            onClick={handleActions}
                          >
                            {t("EditProduct.AbsoluteValue")}
                          </Button>
                        </ButtonGroup>
                      </Grid>
                    )}
                    {product?.useRentalDownPayment && (
                      <Grid item xs={10}>
                        <CustomTextField
                          label={t("Product.RentalDownPayment")}
                          fullWidth
                          isNumeric={true}
                          onChange={productLens.rentalDownPaymentAmount |> set |> onNumericTextFieldChange}
                          value={product?.rentalDownPaymentAmount || 0}
                          startAdornment={<Typography variant="h6">{quote?.currencyCode}</Typography>}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <SimpleSliderWrapper
                        label={t("EditProduct.Term") + " | " + product?.duration + " " + t("EditProduct.Months")}
                        min={product?.minDuration}
                        max={product?.maxDuration}
                        value={product?.duration || 0}
                        aria-valuetext="termSlider"
                        onChange={onLensSliderChange(productLens.duration)}
                        fullWidth
                        showSliderLimits
                        decimalScale={0}
                        step={1}
                        error={!isValid(productValidation?.duration)}
                        helperText={getErrors(productValidation?.duration)}
                        classes={{ root: classes.sliderRoot, thumb: classes.sliderThumb }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        label={t("EditProduct.Period")}
                        options={productPeriodicities}
                        value={product?.periodicity || 0}
                        valueKey="id"
                        labelKey="name"
                        onChange={productLens.periodicity |> set}
                        simpleValue={true}
                        noOptionsText={t("General.AutocompleteNoOptions")}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <DateTime
                        disableToolbar={false}
                        label={t("EditProduct.FirstInstallmentDate")}
                        value={product?.firstInstallmentDate}
                        onChange={productLens.firstInstallmentDate |> set}
                      />
                    </Grid>
                    {(productLens.canAddInsurance |> get || false) && autoAddedInsuranceTypes && allowInsurance && (
                      <Grid item container alignItems="center">
                        <Grid item xs={3} md={2}>
                          <Typography>{t("Product.AddInsurance")}</Typography>
                        </Grid>
                        <Grid item xs={9} md={10}>
                          <Checkbox
                            checked={(productLens.addInsurance |> get) || false}
                            onChange={productLens.addInsurance |> set |> onCheckBoxChange}
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    )}
                    {product?.residualValueVisible && useResidualPercent ? (
                      <Grid item xs={8}>
                        <SimpleSliderWrapper
                          label={
                            product?.showAmountsWithVAT
                              ? t("Product.ResidualValueWithVAT")
                              : t("Product.ResidualValue") +
                                " | " +
                                t("DECIMAL2", { number: product?.residualValuePercent || 0 }) +
                                " " +
                                t("General.Percent") +
                                " (" +
                                quote?.currencyCode +
                                " " +
                                `${
                                  quote?.showAmountsWithVAT
                                    ? t("DECIMAL2", { number: product?.residualValueAmountWithVAT || 0 })
                                    : t("DECIMAL2", { number: product?.residualValueAmount || 0 })
                                }` +
                                ")"
                          }
                          min={product?.residualValueMinPercent}
                          max={product?.residualValueMaxPercentComputed}
                          value={product?.residualValuePercent}
                          onChange={onLensSliderChange(productLens.residualValuePercent)}
                          fullWidth={true}
                          aria-valuetext="balloonAmountSlider"
                          showSliderLimits={true}
                          error={!isValid(productValidation?.residualValuePercent)}
                          helperText={getErrors(productValidation?.residualValuePercent)}
                          decimalScale={2}
                          step={1}
                          classes={{ root: classes.sliderRoot, thumb: classes.sliderThumb }}
                          disabled={settings?.residualValueReadOnly}
                        />
                      </Grid>
                    ) : (
                      product?.residualValueVisible && (
                        <Grid item xs={8}>
                          <CustomTextField
                            label={
                              product?.showAmountsWithVAT
                                ? t("Product.ResidualValueWithVAT")
                                : t("Product.ResidualValue") +
                                  " | " +
                                  t("DECIMAL2", { number: product?.residualValuePercent }) +
                                  " " +
                                  t("General.Percent") +
                                  " (" +
                                  quote?.currencyCode +
                                  " " +
                                  `${
                                    product?.showAmountsWithVAT
                                      ? t("DECIMAL2", { number: product?.residualValueAmountWithVAT })
                                      : t("DECIMAL2", { number: product?.residualValueAmount })
                                  }` +
                                  ")"
                            }
                            fullWidth
                            isNumeric={true}
                            onChange={
                              product?.showAmountsWithVAT
                                ? productLens.residualValueAmountWithVAT |> set |> onNumericTextFieldChange
                                : productLens.residualValueAmount |> set |> onNumericTextFieldChange
                            }
                            value={
                              product?.showAmountsWithVAT
                                ? product?.residualValueAmountWithVAT
                                : product?.residualValueAmount
                            }
                            startAdornment={<Typography variant="h6">{quote?.currencyCode}</Typography>}
                            error={
                              !isValid(
                                product?.showAmountsWithVAT
                                  ? productValidation?.residualValueAmountWithVAT
                                  : productValidation?.residualValueAmount
                              )
                            }
                            helperText={getErrors(
                              product?.showAmountsWithVAT
                                ? productValidation?.residualValueAmountWithVAT
                                : productValidation?.residualValueAmount
                            )}
                            disabled={settings?.residualValueReadOnly}
                          />
                        </Grid>
                      )
                    )}
                    {product?.residualValueVisible && (
                      <Grid item xs={4}>
                        <ButtonGroup className={classes.rightAlign}>
                          <Button
                            id="balloon-percent"
                            variant="contained"
                            size="small"
                            color={useResidualPercent ? "primary" : "inherit"}
                            onClick={handleActions}
                          >
                            {t("EditProduct.Percent")}
                          </Button>
                          <Button
                            id="balloon-abs"
                            size="small"
                            variant="contained"
                            color={useResidualPercent ? "inherit" : "primary"}
                            onClick={handleActions}
                          >
                            {t("EditProduct.AbsoluteValue")}
                          </Button>
                        </ButtonGroup>
                      </Grid>
                    )}
                  </Grid>
                </SimplebarWrapper>
              </Grid>
            </Grid>
          </DialogContent>
        )}
        <DialogActions>
          <EditProductActions
            showAdvancedProductEditAction={true}
            openEditProductLens={openEditProductLens}
            quote={quote}
            productLens={productLens}
            productDirtyInfo={productDirtyInfo}
            settingsLens={settingsLens}
            onSave={onSave}
            onComputeSchedule={onComputeSchedule}
            loading={loading}
            saving={saving}
          />
        </DialogActions>
      </Dialog>
      {(settingsLens.showProductSchedule |> get) && (
        <ProductSchedule showLens={settingsLens.showProductSchedule} quoteProductId={product.quoteProductId} />
      )}
      {(settingsLens.showInsurancePage |> get) && (
        <EditInsuranceDetails
          showInsurancePage={settingsLens.showInsurancePage}
          addInsurance={productLens.addInsurance |> get}
          quoteId={quote.quoteId}
          quoteProductId={product.quoteProductId}
        />
      )}
    </>
  );
}

BasicEditProduct.propTypes = {
  openEditProductLens: PropTypes.object.isRequired,
  quote: PropTypes.object.isRequired,
  productLens: PropTypes.object.isRequired,
  productDirtyInfo: PropTypes.object.isRequired,
  productValidation: PropTypes.object.isRequired,
  productScheduleTypes: PropTypes.array.isRequired,
  productPeriodicities: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onComputeSchedule: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  settingsLens: PropTypes.object.isRequired,
  autoAddedInsuranceTypes: PropTypes.bool.isRequired,
};

export default BasicEditProduct;
